import React, { useState, useEffect } from 'react';
import { 
  Send, Paperclip, Smile, MoreVertical, 
  User, Clock, Tag, ChevronDown,
  MessageCircle, Facebook, MessageSquare, Mail, CheckCircle,
  AlertTriangle, Forward, Download, Loader2, FileText, Search, X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import formatTimestamp from '../../utils/formatTimestamp';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useAuth } from '../../context/AuthContext';
import { sendMessage, assignConversation, updateConversationStatus, forwardConversationEmail, downloadConversation, getTemplates, getConversation, uploadAttachment, getAttachmentUrl, getWhatsAppSenders, getMyProfile } from '../../services/api';

const channelIcons = {
  email: Mail,
  whatsapp: MessageCircle,
  facebook: Facebook,
  chat: MessageSquare
};

const ConversationView = ({ conversation, agents, onUpdate }) => {
  const { user, isAdmin } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [forwardDialog, setForwardDialog] = useState(false);
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [whatsappSenders, setWhatsappSenders] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [allowedSenders, setAllowedSenders] = useState([]); // Agent's allowed senders
  const [senderDropdownOpen, setSenderDropdownOpen] = useState(false);
  const fileInputRef = React.useRef(null);

  // Load WhatsApp senders and user permissions for sender selection
  useEffect(() => {
    if (conversation?.channel === 'whatsapp') {
      Promise.all([
        getWhatsAppSenders(),
        getMyProfile()
      ]).then(([senders, profile]) => {
        setWhatsappSenders(senders);
        const userAllowed = profile.allowed_whatsapp_senders || [];
        setAllowedSenders(userAllowed);
        
        // Filter senders based on user permissions (if any restrictions exist)
        const availableSenders = userAllowed.length > 0 
          ? senders.filter(s => userAllowed.includes(s.phone_number))
          : senders;
        
        // Set default sender - either the one matching conversation or default
        const matchingSender = availableSenders.find(s => s.phone_number === conversation.channel_id);
        const defaultSender = availableSenders.find(s => s.is_default);
        setSelectedSender(matchingSender || defaultSender || availableSenders[0] || null);
      }).catch(() => {});
    }
  }, [conversation?.channel, conversation?.channel_id]);

  useEffect(() => {
    if (conversation) {
      setMessages(conversation.messages || []);
    }
  }, [conversation]);

  // Poll for new messages every 10 seconds while viewing a conversation
  useEffect(() => {
    if (!conversation?.id) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await getConversation(conversation.id);
        if (fresh?.messages?.length !== messages.length) {
          setMessages(fresh.messages || []);
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [conversation?.id, messages.length]);

  useEffect(() => {
    getTemplates().then(setTemplates).catch(() => {});
  }, []);

  const ChannelIcon = channelIcons[conversation?.channel] || Mail;
  const assignedAgent = agents.find(a => a.id === (conversation?.assigned_to || conversation?.assignedTo));

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !conversation) return;
    
    setSending(true);
    try {
      // Pass selected sender for WhatsApp
      const senderNumber = conversation.channel === 'whatsapp' && selectedSender ? selectedSender.phone_number : null;
      const message = await sendMessage(conversation.id, newMessage || (attachments.length > 0 ? `Sent ${attachments.length} file(s)` : ''), attachments, senderNumber);
      setMessages([...messages, message]);
      setNewMessage('');
      setAttachments([]);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          alert(`${file.name} exceeds 25MB limit`);
          continue;
        }
        const result = await uploadAttachment(file);
        uploaded.push(result);
      }
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (error) {
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAssignAgent = async (agentId) => {
    if (!conversation) return;
    try {
      await assignConversation(conversation.id, agentId);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to assign:', error);
      alert(error.response?.data?.detail || 'Failed to assign agent');
    }
  };

  const handleStatusChange = async (status) => {
    if (!conversation) return;
    try {
      await updateConversationStatus(conversation.id, status);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleForwardEmail = async () => {
    if (!forwardEmail.trim() || !conversation) return;
    setForwarding(true);
    try {
      const result = await forwardConversationEmail(conversation.id, {
        recipient_email: forwardEmail,
        note: forwardNote,
      });
      alert(result.message || 'Conversation forwarded');
      setForwardDialog(false);
      setForwardEmail('');
      setForwardNote('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to forward conversation');
    } finally {
      setForwarding(false);
    }
  };

  const handleDownload = async () => {
    if (!conversation) return;
    setDownloading(true);
    try {
      const response = await downloadConversation(conversation.id);
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-${conversation.id.substring(0, 8)}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download conversation');
    } finally {
      setDownloading(false);
    }
  };

  // Check if current user can reply
  const canReply = () => {
    if (isAdmin()) return true;
    return conversation?.assigned_to === user?.id || conversation?.assignedTo === user?.id;
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No conversation selected</h3>
          <p className="text-gray-500">Choose a conversation from the list to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Escalation Banner */}
      {conversation.escalated && conversation.status !== 'resolved' && (
        <div className="px-4 py-2 bg-red-500 text-white flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">
            This conversation has been escalated - unresolved for over 24 hours
          </span>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold",
                conversation.escalated && conversation.status !== 'resolved' ? "bg-red-500" : "bg-[#393d8a]"
              )}>
                {conversation.customer?.avatar || conversation.customer?.name?.charAt(0)}
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100">
                <ChannelIcon className="w-3.5 h-3.5 text-gray-600" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{conversation.customer?.name}</h3>
              <p className="text-sm text-gray-500">{conversation.customer?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Prominent Resolve Button */}
            {conversation.status !== 'resolved' && (
              <Button 
                onClick={() => handleStatusChange('resolved')}
                className="bg-[#30a34b] hover:bg-[#30a34b]/90 text-white gap-2"
                data-testid="resolve-conversation-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Mark as Resolved
              </Button>
            )}
            {conversation.status === 'resolved' && (
              <Button 
                onClick={() => handleStatusChange('open')}
                variant="outline"
                className="gap-2"
              >
                Reopen
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setForwardDialog(true)} data-testid="forward-email-btn">
              <Forward className="w-4 h-4" />
              Forward
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleDownload} disabled={downloading} data-testid="download-thread-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>Mark as resolved</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('pending')}>Mark as pending</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('open')}>Reopen</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Subject and metadata */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{conversation.subject}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {conversation.priority} priority
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTimestamp(conversation.updated_at || conversation.created_at)}
                </span>
              </div>
            </div>
            {isAdmin() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="w-4 h-4" />
                    {assignedAgent ? assignedAgent.name : 'Assign Agent'}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {agents.map((agent) => (
                    <DropdownMenuItem 
                      key={agent.id}
                      onClick={() => handleAssignAgent(agent.id)}
                      className="gap-2"
                    >
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        agent.status === 'online' ? 'bg-green-500' : 
                        agent.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                      )} />
                      {agent.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.map((msg, idx) => (
            <div
              key={msg.id || idx}
              className={cn(
                "flex",
                msg.sender === 'agent' ? 'justify-end' : 'justify-start'
              )}
            >
              {conversation?.channel === 'email' ? (
                <div className={cn("w-full max-w-2xl", msg.sender === 'agent' && "ml-auto")}>
                  <EmailMessageBubble msg={msg} isAgent={msg.sender === 'agent'} />
                  {msg.attachments?.length > 0 && (
                    <AttachmentList attachments={msg.attachments} isAgent={msg.sender === 'agent'} />
                  )}
                </div>
              ) : (
                <div className={cn("max-w-md", msg.sender === 'agent' ? 'ml-auto' : '')}>
                  <div className={cn(
                    "px-4 py-3 rounded-2xl",
                    msg.sender === 'agent'
                      ? 'bg-[#393d8a] text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-900 rounded-bl-md'
                  )}>
                    {msg.text && <p className="text-sm whitespace-pre-wrap">{msg.text}</p>}
                    {msg.attachments?.length > 0 && (
                      <AttachmentList attachments={msg.attachments} isAgent={msg.sender === 'agent'} compact />
                    )}
                    <p className={cn(
                      "text-xs mt-1",
                      msg.sender === 'agent' ? 'text-white/70' : 'text-gray-500'
                    )}>
                      {formatTimestamp(msg.timestamp)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Message Input */}
      {canReply() ? (
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-end gap-3 max-w-3xl mx-auto">
            <div className="flex-1 bg-gray-50 rounded-2xl border border-gray-200 focus-within:ring-2 focus-within:ring-[#393d8a]/20 focus-within:border-[#393d8a] transition-all">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Type your message..."
                rows={1}
                className="w-full px-4 py-3 bg-transparent resize-none focus:outline-none text-sm"
              />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 px-3 pb-2" data-testid="attachment-preview-area">
                  {attachments.map((att, idx) => (
                    <div key={att.id || idx} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs" data-testid={`attachment-preview-${idx}`}>
                      <Paperclip className="w-3 h-3 text-gray-400" />
                      <span className="max-w-[120px] truncate text-gray-700">{att.original_filename}</span>
                      <span className="text-gray-400">{(att.size / 1024).toFixed(0)}KB</span>
                      <button onClick={() => removeAttachment(idx)} className="ml-1 text-gray-400 hover:text-red-500" data-testid={`remove-attachment-${idx}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between px-3 pb-2">
                <div className="flex items-center gap-1">
                  <Popover open={templateOpen} onOpenChange={setTemplateOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-gray-500 hover:text-gray-700" data-testid="template-picker-btn">
                        <FileText className="w-4 h-4" />
                        <span className="text-xs">Templates</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="start" side="top">
                      <div className="p-3 border-b">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={templateSearch}
                            onChange={(e) => setTemplateSearch(e.target.value)}
                            placeholder="Search templates..."
                            className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#393d8a]"
                            data-testid="template-search-input"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {(() => {
                          const channel = conversation?.channel || 'all';
                          const filtered = templates.filter(t => {
                            const matchChannel = t.channels?.includes('all') || t.channels?.includes(channel);
                            const matchSearch = !templateSearch || 
                              t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
                              t.content.toLowerCase().includes(templateSearch.toLowerCase()) ||
                              t.category.toLowerCase().includes(templateSearch.toLowerCase());
                            return matchChannel && matchSearch;
                          });
                          const grouped = filtered.reduce((acc, t) => {
                            const cat = t.category || 'general';
                            if (!acc[cat]) acc[cat] = [];
                            acc[cat].push(t);
                            return acc;
                          }, {});

                          if (Object.keys(grouped).length === 0) {
                            return (
                              <div className="p-4 text-center text-sm text-gray-500">
                                {templates.length === 0 ? 'No templates yet. Ask your admin to create some.' : 'No matching templates.'}
                              </div>
                            );
                          }

                          return Object.entries(grouped).map(([cat, items]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">{cat}</div>
                              {items.map(t => (
                                <button
                                  key={t.id}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                  onClick={() => {
                                    setNewMessage(prev => prev ? prev + '\n' + t.content : t.content);
                                    setTemplateOpen(false);
                                    setTemplateSearch('');
                                  }}
                                  data-testid={`template-item-${t.id}`}
                                >
                                  <p className="text-sm font-medium text-gray-900">{t.name}</p>
                                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{t.content}</p>
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-gray-500 hover:text-gray-700"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="attach-file-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-700" data-testid="emoji-picker-btn">
                        <Smile className="w-4 h-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="start" side="top">
                      <div className="grid grid-cols-8 gap-1">
                        {['😀','😂','😊','😍','🥰','😎','🤔','😢','😡','👍','👎','👏','🙏','❤️','🔥','✅','⭐','🎉','📦','📧','✈️','🏠','📞','💬','🕐','📝','🔔','⚠️','❌','✨','💡','🤝','😄','🙂','😉','😇','🤗','😋','🤩','😏'].map(emoji => (
                          <button
                            key={emoji}
                            className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                            onClick={() => setNewMessage(prev => prev + emoji)}
                            data-testid={`emoji-${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                {/* WhatsApp Sender Selection */}
                {conversation?.channel === 'whatsapp' && whatsappSenders.length > 0 && (
                  <Popover open={senderDropdownOpen} onOpenChange={setSenderDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 gap-1.5 text-gray-500 hover:text-gray-700 max-w-[200px]"
                        data-testid="whatsapp-sender-selector"
                      >
                        <MessageCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <span className="text-xs truncate">
                          {selectedSender ? selectedSender.name || selectedSender.phone_number : 'Select sender'}
                        </span>
                        <ChevronDown className="w-3 h-3 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-0" align="end" side="top">
                      <div className="p-2 border-b bg-gray-50">
                        <p className="text-xs font-medium text-gray-600">Send reply from:</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {(() => {
                          // Filter senders based on agent permissions
                          const availableSenders = allowedSenders.length > 0
                            ? whatsappSenders.filter(s => allowedSenders.includes(s.phone_number))
                            : whatsappSenders;
                          
                          if (availableSenders.length === 0) {
                            return (
                              <div className="p-3 text-center text-xs text-gray-500">
                                No WhatsApp senders available for your account.
                              </div>
                            );
                          }
                          
                          return availableSenders.map(sender => (
                            <button
                              key={sender.id}
                              className={cn(
                                "w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0",
                                selectedSender?.id === sender.id && "bg-green-50"
                              )}
                              onClick={() => {
                                setSelectedSender(sender);
                                setSenderDropdownOpen(false);
                              }}
                              data-testid={`sender-option-${sender.id}`}
                            >
                              <div className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                                selectedSender?.id === sender.id ? "bg-green-100" : "bg-gray-100"
                              )}>
                                <MessageCircle className={cn(
                                  "w-4 h-4",
                                  selectedSender?.id === sender.id ? "text-green-600" : "text-gray-400"
                                )} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{sender.name}</p>
                                <p className="text-xs text-gray-500 truncate">{sender.phone_number}</p>
                              </div>
                              {sender.is_default && (
                                <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium shrink-0">Default</span>
                              )}
                              {selectedSender?.id === sender.id && (
                                <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                              )}
                            </button>
                          ));
                        })()}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
            <Button 
              onClick={handleSendMessage}
              disabled={sending || (!newMessage.trim() && attachments.length === 0)}
              className="h-12 w-12 rounded-full bg-[#eeb224] hover:bg-[#eeb224]/90 shadow-lg shadow-[#eeb224]/30"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4 border-t border-gray-200 bg-yellow-50 text-center">
          <p className="text-yellow-700 text-sm">
            This conversation is not assigned to you. Contact an admin to be assigned.
          </p>
        </div>
      )}

      {/* Forward Email Dialog */}
      <Dialog open={forwardDialog} onOpenChange={setForwardDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Forward via Email</DialogTitle>
            <DialogDescription>Send this conversation thread to an email address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={forwardEmail}
                onChange={(e) => setForwardEmail(e.target.value)}
                placeholder="manager@postaruba.com"
                data-testid="forward-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
                placeholder="Add context for the recipient..."
                rows={3}
                className="resize-none"
                data-testid="forward-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForwardDialog(false)}>Cancel</Button>
            <Button
              onClick={handleForwardEmail}
              disabled={forwarding || !forwardEmail.trim()}
              className="bg-[#393d8a] hover:bg-[#393d8a]/90 gap-2"
              data-testid="forward-send-btn"
            >
              {forwarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Forward className="w-4 h-4" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Renders email messages in an Outlook-like iframe style
const EmailMessageBubble = ({ msg, isAgent }) => {
  const iframeRef = React.useRef(null);
  const [height, setHeight] = React.useState(120);
  const hasHtml = msg.html && msg.html.trim().length > 0;

  // Replace cid: references with actual attachment URLs
  const processHtmlContent = (html, attachments) => {
    if (!html || !attachments?.length) return html;
    
    let processed = html;
    attachments.forEach(att => {
      if (att.content_id) {
        // Replace cid:content_id with the actual download URL
        const cidPattern = new RegExp(`cid:${att.content_id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
        const downloadUrl = getAttachmentUrl(att.filename);
        processed = processed.replace(cidPattern, downloadUrl);
      }
    });
    return processed;
  };

  React.useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // Process HTML to replace CID references
    const processedHtml = hasHtml ? processHtmlContent(msg.html, msg.attachments) : null;

    const content = processedHtml
      ? processedHtml
      : `<p style="margin:0;white-space:pre-wrap;">${(msg.text || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</p>`;

    const wrappedHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { margin: 0; padding: 16px; font-family: 'Segoe UI', Calibri, -apple-system, sans-serif; font-size: 14px; line-height: 1.6; color: #1a1a1a; word-break: break-word; overflow-wrap: break-word; background: #fff; }
  img { max-width: 100%; height: auto; }
  a { color: #0563C1; text-decoration: underline; }
  table { max-width: 100% !important; border-collapse: collapse; }
  td, th { padding: 4px 8px; }
  pre, code { white-space: pre-wrap; max-width: 100%; overflow-x: auto; background: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-size: 13px; }
  blockquote { border-left: 3px solid #0563C1; margin: 12px 0; padding: 4px 16px; color: #555; background: #f9f9f9; }
  hr { border: none; border-top: 1px solid #e5e5e5; margin: 12px 0; }
  p { margin: 0 0 8px 0; }
</style></head><body>${content}</body></html>`;

    doc.open();
    doc.write(wrappedHtml);
    doc.close();

    const resize = () => {
      try {
        const h = doc.documentElement?.scrollHeight || doc.body?.scrollHeight || 120;
        setHeight(Math.min(Math.max(h, 60), 600));
      } catch {}
    };

    iframe.onload = resize;
    setTimeout(resize, 150);
    setTimeout(resize, 500);
  }, [msg.html, msg.text, msg.attachments, hasHtml]);

  return (
    <div className={cn("w-full max-w-2xl", isAgent && "ml-auto")} data-testid="email-message-bubble">
      <div className={cn(
        "border rounded-lg overflow-hidden shadow-sm",
        isAgent ? "border-[#393d8a]/30 bg-[#393d8a]/5" : "border-gray-200 bg-white"
      )}>
        <div className={cn(
          "flex items-center gap-2 px-3 py-2 border-b",
          isAgent ? "bg-[#393d8a]/10 border-[#393d8a]/20" : "bg-gray-50 border-gray-200"
        )}>
          <Mail className={cn("w-3.5 h-3.5", isAgent ? "text-[#393d8a]" : "text-gray-400")} />
          <span className={cn("text-xs font-medium", isAgent ? "text-[#393d8a]" : "text-gray-600")}>
            {isAgent ? 'Sent' : 'Received'}
          </span>
          <span className="text-xs text-gray-400 ml-auto">{formatTimestamp(msg.timestamp)}</span>
        </div>
        <iframe
          ref={iframeRef}
          title="email-content"
          sandbox="allow-same-origin"
          style={{ width: '100%', height: `${height}px`, border: 'none', display: 'block' }}
        />
      </div>
    </div>
  );
};

// Renders a list of downloadable attachments
const AttachmentList = ({ attachments, isAgent, compact }) => {
  if (!attachments?.length) return null;

  const isImage = (ct) => ct && ct.startsWith('image/');

  return (
    <div className={cn("flex flex-wrap gap-2", compact ? "mt-2" : "mt-1 px-1")} data-testid="attachment-list">
      {attachments.map((att, idx) => {
        const url = getAttachmentUrl(att.filename);
        if (isImage(att.content_type)) {
          return (
            <a key={att.id || idx} href={url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`attachment-download-${idx}`}>
              <img
                src={url}
                alt={att.original_filename}
                className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-200 object-cover"
              />
            </a>
          );
        }
        return (
          <a
            key={att.id || idx}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors",
              isAgent && !compact
                ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                : compact && isAgent
                ? "bg-white/10 hover:bg-white/20 text-white/90 border border-white/20"
                : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
            )}
            data-testid={`attachment-download-${idx}`}
          >
            <Download className="w-3.5 h-3.5 shrink-0" />
            <span className="max-w-[140px] truncate">{att.original_filename}</span>
            <span className="opacity-60">{(att.size / 1024).toFixed(0)}KB</span>
          </a>
        );
      })}
    </div>
  );
};

export default ConversationView;
